import Vue from 'vue'
import Component from 'vue-class-component'
import autobahn, {Connection, IPublishOptions, ISubscribeOptions, Session, SubscribeHandler} from 'autobahn';
import KeyValuePair from '@/modules/core/support/KeyValuePair';

/**
 * Mixin con funciones de utilidad para iniciar sesión en el enrutador
 * WAMP y poder enviar eventos o suscribirse a ellos además de la
 * posibilidad de registrar RPCs.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component
export default class Wamp extends Vue {
    /**
     * Sesión actualmente activa en el enrutador WAMP, si no se ha iniciado
     * sesión o ya ha sido cerrada la propiedad estará vacía
     *
     * @var { Session | null }
     */
    public wampSession: Session | null = null

    /**
     * Conexión actualmente abierta con el enrutador WAMP. Si ya existe
     * alguna conexión no se creará una nueva
     *
     * @var { Connection | null }
     */
    public wampConnection: Connection | null = null

    /**
     * Acceso directo con las funcionalidades WAMP
     */
    public get $wamp () {
        // Comprueba que exista una sesión iniciada en el enrutador WAMP
        const checkSession = () => {
            if (!this.wampSession) {
                throw new Error('No existe ninguna sesión en el enrutador WAMP')
            }
        }

        return {
            connect: () => this.initWampSession(),
            close: () => this.closeWampSession(),
            session: this.wampSession,
            subscribe: (topic: string, handler: SubscribeHandler, options: ISubscribeOptions = {}) => {
                checkSession()

                return this.wampSession!.subscribe(topic, handler, options)
            },
            publish: (topic: string, args?: any[], kwargs?: any, options?: IPublishOptions) => {
                checkSession()

                return this.wampSession!.publish(topic, args, kwargs, options)
            },
        }
    }

    /**
     * Cierra la sesión actual
     */
    public closeWampSession () {
        return new Promise(resolve => {
            if (!this.wampConnection) {
                return resolve(false)
            }

            this.wampConnection!.onclose = (reason: string) => {
                resolve(true)
                this.wampSession = null
                return true

            }
            this.wampConnection!.close()
        })
    }

    /**
     * Inicia sesión en el enrutador WAMP, si ya existe alguna activa
     * no se volverá a crear.
     */
    public async initWampSession (): Promise<autobahn.Session> {
        // Ya existe una sesión creada
        if (this.wampSession) {
            return this.wampSession;
        }

        const url = process.env.VUE_APP_WAMP_ROUTER_URL
        const realm = process.env.VUE_APP_WAMP_REALM
        const authid = process.env.VUE_APP_WAMP_AUTHID
        const ticket = process.env.VUE_APP_WAMP_AUTH_TICKET || ''

        if (!url) {
            throw new Error('No se ha especificado la URL para el enrutador WAMP')
        }

        if (!realm) {
            throw new Error('No se ha especificado ningún reino WAMP al cual acceder')
        }

        if (!authid) {
            throw new Error('No se ha especificado ningún ID de autenticación')
        }

        // Crea una nueva conexión con el enrutador WAMP
        const connection = new autobahn.Connection({
            url,
            realm,
            authmethods: ['ticket'],
            authid,
            onchallenge: () => ticket,
        });

        this.wampConnection = connection
        const promise = new Promise<autobahn.Session>((resolve, reject) => {
            // Cuando la conexión es abierta la promesa es resulta con la nueva
            // sesión creada
            connection.onopen = (session, details) => {
                this.log('INFO', `Sesión iniciada en el enrutador WAMP con id ${session.id}`);
                this.log('INFO', `Autenticado con el método "${details.authmethod}" y el rol ${details.authrole}`);
                resolve(this.wampSession = session);
            };

            connection.onclose = (reason) => {
                reject(new Error(reason));

                this.log('WARN', `La  conexión ha sido cerrada motivo: ${reason}`)

                return false
            };
        });

        connection.open();

        return promise;
    }

    /**
     * Registra eventos producidos por el componente WAMP
     *
     * @param level
     * @param message
     */
    public log (level: 'INFO' | 'WARN', message: string) {
        // tslint:disable-next-line:no-console
        console.log(`[${level}] ${message}`)
    }

    /**
     * Cierra la conexión con el enrutador WAMP cuando el componente es destruido
     */
    public async destroyed () {
        this.$wamp.close()
    }
}
